import Icon from "@components/Icon";
import { FORGETPASSWORD, HOME, RESET_PASSWORD, SIGNUP, VERIFY_OTP } from "@constants/internal-route.constant";
import { IPublicModule } from "@interface/billing.interface";
import { useColorMode } from "@layout/console/useColorMode";
import { ModuleService } from "@services/api/Module.service";
import { ENV } from "config/ENV.config";
import { CSSProperties, useEffect, useState } from "react";
import { Link, Outlet, useLocation } from "react-router-dom";
import { BLACK, hexToRgb, mix, RGB, rgbList, rgbToHex, textOn, WHITE } from "utility/color";
import PublicFooter from "./PublicFooter";

type Pitch = { title: [string, string]; sub: string };

// The brand panel's headline, per screen. Two lines, as the panel sets them large.
const SIGN_IN: Pitch = {
  title: ["Open your", "business’s books."],
  sub: "Sales, people, accounts and files — one sign-in for everything your business runs on.",
};
const PITCH: Record<string, Pitch> = {
  [SIGNUP]: {
    title: ["Start your", "30-day trial."],
    sub: "Set up your business in minutes, with no card needed. Turn on the apps you use and pay monthly for what you use.",
  },
  [FORGETPASSWORD]: {
    title: ["Back in", "a minute."],
    sub: "Reset your password with a one-time code. Your workspace stays exactly as you left it.",
  },
};
PITCH[VERIFY_OTP] = PITCH[FORGETPASSWORD];
PITCH[RESET_PASSWORD] = PITCH[FORGETPASSWORD];

// The brand's two colours (ErpBoi: the logo's blue and navy; another install sets its own through
// REACT_APP_BRAND_COLOR / _DARK). Everything on these screens derives from them: the panel's
// gradient and glow, the ticks, and the form's button, links and focus rings.
const MAIN: RGB = hexToRgb(ENV.brandColor) || [1, 95, 238];
const DARK: RGB = hexToRgb(ENV.brandColorDark) || [1, 23, 70];
const AUTH_VARS = {
  "--erp-auth-dark": rgbToHex(DARK),
  "--erp-auth-main": rgbToHex(MAIN),
  "--erp-auth-main-rgb": rgbList(MAIN),
  "--erp-auth-deep": rgbToHex(mix(MAIN, DARK, 0.35)),
  "--erp-auth-light-rgb": rgbList(mix(MAIN, WHITE, 0.45)),
  "--bs-primary": rgbToHex(MAIN),
  "--bs-primary-rgb": rgbList(MAIN),
  "--erp-brand-hover": rgbToHex(mix(MAIN, BLACK, 0.15)),
  "--erp-brand-active": rgbToHex(mix(MAIN, BLACK, 0.25)),
  "--erp-brand-contrast": rgbToHex(textOn(MAIN)),
  "--bs-link-color-rgb": rgbList(MAIN),
  "--bs-link-hover-color-rgb": rgbList(mix(MAIN, BLACK, 0.2)),
} as CSSProperties;

/**
 * The sign-in screens (login, signup, password recovery, /app-login): a full-page split.
 * Left, the brand panel — logo, the screen's headline, and the apps this one login opens (the
 * visible public.modules, DB-driven and allowlist-filtered). Right, the form. Below 992px the
 * panel shrinks to a header band over the form.
 */
const AuthLayout = () => {
  const { pathname } = useLocation();
  useColorMode();

  const [apps, setApps] = useState<IPublicModule[]>([]);
  useEffect(() => {
    ModuleService.getPublicList()
      .then((res) => setApps(res?.body || []))
      .catch(() => setApps([]));
  }, []);

  const pitch = PITCH[pathname] || SIGN_IN;
  const wide = pathname === SIGNUP;

  return (
    <div className="erp-auth-page" style={AUTH_VARS}>
      <aside className="erp-auth-brand" data-bs-theme="dark">
        <div className="erp-auth-brand__inner">
          <Link to={HOME} className="erp-auth-brand__logo" aria-label={ENV.brandName}>
            <img src={ENV.brandLogo} alt={ENV.brandName} />
          </Link>

          <div className="erp-auth-brand__copy">
            <h2 className="erp-auth-brand__title">
              {pitch.title[0]}
              <br />
              {pitch.title[1]}
            </h2>
            <p className="erp-auth-brand__sub">{pitch.sub}</p>
            {apps.length ? (
              <ul className="erp-auth-brand__list">
                {apps.map((a) => (
                  <li key={a.code} title={a.description || a.title}>
                    <Icon icon="check-circle-fill" />
                    <span>{a.title}</span>
                  </li>
                ))}
              </ul>
            ) : null}
          </div>

          <PublicFooter className="erp-auth-brand__foot" />
        </div>
      </aside>

      <main className="erp-auth-main">
        <div className={`erp-auth-form${wide ? " erp-auth-form--wide" : ""}`}>
          <Outlet />
          <p className="erp-auth-secure">
            <Icon icon="shield-lock" size={14} /> Secure sign-in · Your session is protected.
          </p>
        </div>
        {/* Below 992px the panel is a header band without its footer: the footer moves here. */}
        <PublicFooter className="erp-auth-main__foot" />
      </main>
    </div>
  );
};

export default AuthLayout;
